import React, { createContext } from 'react'
import { useSelector } from 'react-redux'
import { Route, RouteComponentProps, Router, Switch } from 'react-router-dom'
import { utils } from '@ims/1edtech-frontend-common'

import { FeatureFlagsContext } from 'lib/components/FeatureFlags'
import { OrgActionsContext } from 'lib/components/OrgActions'
import { RootState } from 'lib/store/rootReducer'
import { history } from 'lib/utils/navigation'
import MainRouter from 'domains/application/components/MainRouter'
import PrintRouter from 'domains/application/components/PrintRouter'
import {
  FRESHDESK_JWT_LOGIN,
  LOGIN_ROUTE,
  LOGIN_ROUTES,
  LOGOUT_ROUTE,
  LTI_LOGIN_REDIRECT_ROUTE,
  OAUTH_REDIRECT_ROUTE,
  PRINT_ROOT_ROUTE,
} from 'domains/application/navigation/routes'
import { applicationPropSelector } from 'domains/application/selectors/application'
import { useAuth0AutoLogin } from 'domains/authentication/hooks/useAutoAuth0Login'
import FreshdeskJWTLoginScreen from 'domains/authentication/screens/FreshdeskJWTLoginScreen'
import LoginScreen from 'domains/authentication/screens/LoginScreen'
import LogoutScreen from 'domains/authentication/screens/LogoutScreen'
import LTILoginScreen from 'domains/authentication/screens/LTILoginScreen'
import OAuth2RedirectScreen from 'domains/authentication/screens/OAuth2RedirectScreen'
import { myOrganizationPropSelector } from 'domains/authentication/selectors/organizations'

export const RouterContext = createContext({})

export default function AppScreen(props: any) {
  const me = utils.convertToInt(useSelector(applicationPropSelector('me')))
  const featureFlags = useSelector((state: RootState) =>
    myOrganizationPropSelector('featureFlags')(state, props),
  )
  const orgActions = useSelector((state: RootState) =>
    myOrganizationPropSelector('actions')(state, props),
  )
  const orgOverrides = useSelector((state: RootState) =>
    myOrganizationPropSelector('overrides')(state, props),
  )

  const renderLoggedInRouters = (routeProps: RouteComponentProps) => {
    if (routeProps.location.pathname.startsWith(PRINT_ROOT_ROUTE)) {
      return <Route component={PrintRouter} />
    }

    return <Route component={MainRouter} />
  }

  const renderRoute = (routeProps: RouteComponentProps) => {
    return (
      <RouterContext.Provider value={routeProps}>
        <Switch location={routeProps.location}>
          <Route exact path={LOGIN_ROUTE} component={LoginScreen} />
          <Route exact path={LOGOUT_ROUTE} component={LogoutScreen} />
          <Route path={OAUTH_REDIRECT_ROUTE} component={OAuth2RedirectScreen} />
          <Route path={LTI_LOGIN_REDIRECT_ROUTE} component={LTILoginScreen} />
          <Route
            path={FRESHDESK_JWT_LOGIN}
            component={FreshdeskJWTLoginScreen}
          />
          {me > 0 && <Route render={renderLoggedInRouters} />}
        </Switch>
      </RouterContext.Provider>
    )
  }

  useAuth0AutoLogin({
    enabled: !LOGIN_ROUTES.includes(history.location.pathname),
  })

  return (
    <FeatureFlagsContext.Provider value={featureFlags || []}>
      <OrgActionsContext.Provider
        value={{ actions: orgActions || [], overrides: orgOverrides || [] }}
      >
        <Router history={history}>
          <Route render={renderRoute} />
        </Router>
      </OrgActionsContext.Provider>
    </FeatureFlagsContext.Provider>
  )
}
